import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Divider,
  Collapse,
  Box,
  Tooltip,
  IconButton,
} from "@mui/material"
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Work as WorkIcon,
  Group as GroupIcon,
  Search as SearchIcon,
  Assignment as AssignmentIcon,
  AttachMoney as MoneyIcon,
  ExpandLess,
  ExpandMore,
  FiberNew as NewIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Receipt as ReceiptIcon,
  Work,
  Receipt,
  Timelapse,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  FileDownload as DownloadIcon,
} from "@mui/icons-material"
import { useState, useRef } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import * as XLSX from "xlsx"
import api from "../../utils/api"

function Sidebar({ mobileOpen, onMobileClose, drawerWidth, isCollapsed, onToggleCollapse, onWidthChange, userRole }) {
  const location = useLocation()
  const [openJobs, setOpenJobs] = useState(location.pathname.startsWith("/jobs"))
  const [openProcessing, setOpenProcessing] = useState(location.pathname.startsWith("/processing"))
  const [openFees, setOpenFees] = useState(location.pathname.startsWith("/fees"))
  const [openSecondary, setOpenSecondary] = useState(location.pathname.startsWith("/secondary-jobs"))
  const [isExporting, setIsExporting] = useState(false)
  const navigate = useNavigate()

  const isResizingRef = useRef(false)

  const handleExportAllJobs = async () => {
    try {
      setIsExporting(true)
      const response = await api.get("/jobs/export")
      const jobs = response.data || []

      if (!Array.isArray(jobs) || jobs.length === 0) {
        alert("No jobs found to export.")
        return
      }

      const excelRows = jobs.map((job) => {
        const secondaryUserNames = Array.isArray(job.secondaryUser) && job.secondaryUser.length > 0
          ? job.secondaryUser.map((u) => u.name || u.phoneNumber || "User").join(", ")
          : "None"

        const commentsFormatted = Array.isArray(job.comments) && job.comments.length > 0
          ? job.comments
              .map((c) => {
                const dateStr = c.commentedAt ? new Date(c.commentedAt).toLocaleString("en-IN") : ""
                const author = c.commentedBy?.name || "Unknown"
                return `[${dateStr} by ${author}]: ${c.commentText || ""}`
              })
              .join("\n")
          : "No comments"

        const historyFormatted = Array.isArray(job.statusHistory) && job.statusHistory.length > 0
          ? job.statusHistory
              .map((h) => {
                const dateStr = h.at ? new Date(h.at).toLocaleString("en-IN") : ""
                const author = h.by?.name || "Unknown"
                const typeStr = h.type === "subStatus" ? "Sub-status" : "Status"
                const reasonStr = h.reason ? ` (Reason: ${h.reason})` : ""
                return `[${dateStr} by ${author}]: ${typeStr} ${h.from || "none"} → ${h.to || ""}${reasonStr}`
              })
              .join("\n")
          : "No history"

        return {
          "Job ID": job.JobId || job.jobId || "N/A",
          "Client Name": job.clientName || "N/A",
          "Client Phone": job.clientPhoneNumber || "N/A",
          "Client PAN": job.clientPan || "N/A",
          "Client GSTIN": job.clientGstin || "N/A",
          "Client Trade Name": job.clientTradeName || "N/A",
          "Assigned To": job.assignedToName ? `${job.assignedToName}${job.assignedToRole ? ` (${job.assignedToRole})` : ""}` : "Unassigned",
          "Secondary Users": secondaryUserNames,
          "Status": job.status || "N/A",
          "Sub Status": job.subStatus || "None",
          "Fees": job.fees ?? 0,
          "Fee Status": job.feeStatus || "pending",
          "Comments": commentsFormatted,
          "Status Change History": historyFormatted,
          "Created At": job.createdAt ? new Date(job.createdAt).toLocaleString("en-IN") : "N/A",
        }
      })

      const worksheet = XLSX.utils.json_to_sheet(excelRows)

      worksheet["!cols"] = [
        { wch: 12 },
        { wch: 22 },
        { wch: 15 },
        { wch: 14 },
        { wch: 18 },
        { wch: 20 },
        { wch: 22 },
        { wch: 25 },
        { wch: 14 },
        { wch: 22 },
        { wch: 10 },
        { wch: 12 },
        { wch: 45 },
        { wch: 50 },
        { wch: 22 },
      ]

      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, "All Jobs Backup")

      const todayStr = new Date().toISOString().split("T")[0]
      XLSX.writeFile(workbook, `Fiscale_All_Jobs_Backup_${todayStr}.xlsx`)
    } catch (error) {
      console.error("Error exporting jobs to excel:", error)
      alert("Failed to export jobs. Please try again.")
    } finally {
      setIsExporting(false)
    }
  }

  const handleProcessingClick = () => {
    setOpenProcessing(!openProcessing)
  }

  const handleFeesClick = () => {
    setOpenFees(!openFees)
  }

  // Handle Resize Dragging
  const handleMouseDown = (e) => {
    e.preventDefault()
    isResizingRef.current = true
    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)
  }

  const handleMouseMove = (e) => {
    if (!isResizingRef.current) return
    const newWidth = Math.min(Math.max(e.clientX, 190), 450)
    if (onWidthChange) {
      onWidthChange(newWidth)
    }
  }

  const handleMouseUp = () => {
    isResizingRef.current = false
    document.removeEventListener("mousemove", handleMouseMove)
    document.removeEventListener("mouseup", handleMouseUp)
  }

  const handleDoubleClickResize = () => {
    if (onWidthChange) {
      onWidthChange(260) // Reset to default width
    }
  }

  const getMenuItems = () => {
    const commonItems = [{ text: "Dashboard", icon: <DashboardIcon />, path: "/dashboard" }]

    if (userRole === "admin") {
      return [
        ...commonItems,
        { text: "Clients", icon: <PeopleIcon />, path: "/clients" },
        {
          text: "Jobs",
          icon: <WorkIcon />,
          hasSubmenu: true,
          submenu: [
            { text: "All Jobs", path: "/jobs" },
            { text: "New", path: "/jobs/new" },
            { text: "Processing", path: "/jobs/processing" },
            { text: "Completed", path: "/jobs/completed" },
            { text: "Cancelled", path: "/jobs/cancelled" },
          ],
        },
        { text: "Exceeded Due Date", icon: <Timelapse />, path: "/exceeded-due-date-jobs" },
        { text: "Predefined List", icon: <AssignmentIcon />, path: "/predefined-list" },
        { text: "Users", icon: <GroupIcon />, path: "/users" },
        { text: "Receipts", icon: <Receipt />, path: "/all-receipts" },
        {
          text: "Fees",
          icon: <MoneyIcon />,
          hasSubmenu: true,
          submenu: [
            { text: "Pending", path: "/fees/pending" },
            { text: "Received", path: "/fees/received" },
          ],
        },
        { text: "Search Client", icon: <SearchIcon />, path: "/search-client" },
        { text: "Search Jobs", icon: <SearchIcon />, path: "/search-jobs" },
        {
          text: isExporting ? "Exporting..." : "Export All Jobs (Excel)",
          icon: <DownloadIcon />,
          isAction: true,
          action: handleExportAllJobs,
        },
      ]
    }

    if (userRole === "normal") {
      return [
        ...commonItems,
        { text: "New Jobs", icon: <NewIcon />, path: "/new-jobs" },
        {
          text: "Processing Jobs",
          icon: <ScheduleIcon />,
          hasSubmenu: true,
          submenu: [
            { text: "Accepted", path: "/processing/accepted" },
            { text: "Documents Pending", path: "/processing/documents-pending" },
            { text: "Tax Pending", path: "/processing/tax-pending" },
            { text: "Verification Pending", path: "/processing/verification-pending" },
            { text: "Client Not Responding", path: "/processing/client-not-responding" },
            { text: "Kept on Hold", path: "/processing/kept-on-hold" },
          ],
        },
        { text: "Job Completed", icon: <CheckCircleIcon />, path: "/job-completed" },
        { text: "Job Cancelled by Client", icon: <CancelIcon />, path: "/job-cancelled" },
        {
          text: "Fees",
          icon: <MoneyIcon />,
          hasSubmenu: true,
          submenu: [
            { text: "Pending", path: "/fees/pending" },
            { text: "Received", path: "/fees/received" },
          ],
        },
        {
          text: "Assigned as Secondary",
          icon: <Work />,
          hasSubmenu: true,
          submenu: [
            { text: "New", path: "/secondary-jobs/new" },
            { text: "Processing", path: "/secondary-jobs/processing" },
            { text: "Completed", path: "/secondary-jobs/completed" },
          ],
        },
        { text: "Search Client", icon: <SearchIcon />, path: "/search-client" },
        { text: "Search Jobs", icon: <SearchIcon />, path: "/search-jobs" },
      ]
    }

    if (userRole === "feesCollector") {
      return [
        ...commonItems,
        { text: "Add Fee Receipts", icon: <ReceiptIcon />, path: "/add-fee-receipts" },
        { text: "Search Client", icon: <SearchIcon />, path: "/search-client" },
        { text: "Search Jobs", icon: <SearchIcon />, path: "/search-jobs" },
      ]
    }

    if (userRole === "feesSettler") {
      return [
        ...commonItems,
        { text: "Search Client", icon: <SearchIcon />, path: "/search-client" },
        { text: "Search Jobs", icon: <SearchIcon />, path: "/search-jobs" },
      ]
    }

    return commonItems
  }

  const menuItems = getMenuItems()

  const renderMenuItem = (item, index) => {
    if (item.hasSubmenu) {
      let isOpen = false
      let handleClick = () => {}

      if (item.text === "Processing Jobs") {
        isOpen = openProcessing
        handleClick = handleProcessingClick
      } else if (item.text === "Fees") {
        isOpen = openFees
        handleClick = handleFeesClick
      } else if (item.text === "Jobs") {
        isOpen = openJobs
        handleClick = () => setOpenJobs(!openJobs)
      } else if (item.text === "Assigned as Secondary") {
        isOpen = openSecondary
        handleClick = () => setOpenSecondary(!openSecondary)
      }

      const buttonContent = (
        <ListItemButton
          onClick={() => {
            if (isCollapsed && onToggleCollapse) {
              onToggleCollapse()
            }
            handleClick()
          }}
        >
          <ListItemIcon>{item.icon}</ListItemIcon>
          {!isCollapsed && (
            <>
              <ListItemText primary={item.text} />
              {isOpen ? <ExpandLess /> : <ExpandMore />}
            </>
          )}
        </ListItemButton>
      )

      return (
        <Box key={index}>
          <ListItem disablePadding>
            {isCollapsed ? (
              <Tooltip title={item.text} placement="right" arrow>
                {buttonContent}
              </Tooltip>
            ) : (
              buttonContent
            )}
          </ListItem>
          {!isCollapsed && (
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {item.submenu.map((subItem, subIndex) => (
                  <ListItem key={subIndex} disablePadding>
                    <ListItemButton
                      sx={{ pl: 4 }}
                      selected={location.pathname === subItem.path}
                      onClick={() => {
                        navigate(subItem.path)
                        if (mobileOpen) onMobileClose()
                      }}
                    >
                      <ListItemText primary={subItem.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          )}
        </Box>
      )
    }

    if (item.isAction) {
      const actionItemContent = (
        <ListItemButton
          onClick={() => {
            if (item.action) item.action()
            if (mobileOpen) onMobileClose()
          }}
          disabled={isExporting}
        >
          <ListItemIcon>{item.icon}</ListItemIcon>
          {!isCollapsed && <ListItemText primary={item.text} />}
        </ListItemButton>
      )

      return (
        <ListItem key={index} disablePadding>
          {isCollapsed ? (
            <Tooltip title={item.text} placement="right" arrow>
              {actionItemContent}
            </Tooltip>
          ) : (
            actionItemContent
          )}
        </ListItem>
      )
    }

    const singleItemContent = (
      <ListItemButton
        selected={location.pathname === item.path}
        onClick={() => {
          navigate(item.path)
          if (mobileOpen) onMobileClose()
        }}
      >
        <ListItemIcon>{item.icon}</ListItemIcon>
        {!isCollapsed && <ListItemText primary={item.text} />}
      </ListItemButton>
    )

    return (
      <ListItem key={index} disablePadding>
        {isCollapsed ? (
          <Tooltip title={item.text} placement="right" arrow>
            {singleItemContent}
          </Tooltip>
        ) : (
          singleItemContent
        )}
      </ListItem>
    )
  }

  const drawerContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Toolbar sx={{ display: "flex", alignItems: "center", justifyContent: isCollapsed ? "center" : "flex-end", px: 1 }}>
        <IconButton size="small" onClick={onToggleCollapse}>
          {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Toolbar>
      <Divider />

      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        <List>{menuItems.map((item, index) => renderMenuItem(item, index))}</List>
      </Box>
    </Box>
  )

  return (
    <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": { boxSizing: "border-box", width: 280 },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop drawer - Pinned fixed to screen height */}
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: "none", sm: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            height: "100vh",
            position: "fixed",
            top: 0,
            left: 0,
            transition: isResizingRef.current ? "none" : "width 0.2s ease-in-out",
            overflowX: "hidden",
          },
        }}
      >
        {drawerContent}

        {/* Resizable Drag Edge Handle */}
        {!isCollapsed && (
          <Tooltip title="Drag to resize / Double-click to reset" placement="right">
            <Box
              onMouseDown={handleMouseDown}
              onDoubleClick={handleDoubleClickResize}
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
                bottom: 0,
                width: 6,
                cursor: "col-resize",
                zIndex: 1200,
                transition: "background-color 0.15s",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.15)",
                },
                "&:active": {
                  backgroundColor: "rgba(0, 0, 0, 0.25)",
                },
              }}
            />
          </Tooltip>
        )}
      </Drawer>
    </Box>
  )
}

export default Sidebar
